import { useState, useEffect } from "react";
import {
  Play, Settings, Clock, CheckCircle2, XCircle, Loader2, Plus, X,
  RefreshCcw, AlertCircle, ExternalLink, Calendar as CalIcon
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaScraperManager = () => {
  const { getAuthHeader } = useAuth();

  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Editable config state
  const [editAuthors, setEditAuthors] = useState([]);
  const [editSites, setEditSites] = useState([]);
  const [editKeywords, setEditKeywords] = useState([]);
  const [editLimit, setEditLimit] = useState(40);
  const [editSchedEnabled, setEditSchedEnabled] = useState(true);
  const [editCron, setEditCron] = useState("0 9 * * 1");
  const [newAuthor, setNewAuthor] = useState("");
  const [newSite, setNewSite] = useState("");
  const [newKeyword, setNewKeyword] = useState("");

  useEffect(() => {
    loadAll();
    // Poll pending list every 20s while a scan may be running
    const interval = setInterval(() => {
      loadPending();
      loadRuns();
    }, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadConfig(), loadPending(), loadRuns()]);
    setLoading(false);
  };

  const loadConfig = async () => {
    try {
      const res = await axios.get(`${API}/scraper/config`, { headers: getAuthHeader() });
      setConfig(res.data.config);
    } catch (e) {
      toast.error("Failed to load scraper config");
    }
  };

  const loadPending = async () => {
    try {
      const res = await axios.get(`${API}/scraper/pending?limit=200`, { headers: getAuthHeader() });
      setPending(res.data.resources || []);
      setPendingTotal(res.data.total || 0);
    } catch (e) { /* silent */ }
  };

  const loadRuns = async () => {
    try {
      const res = await axios.get(`${API}/scraper/runs?limit=10`, { headers: getAuthHeader() });
      setRuns(res.data.runs || []);
    } catch (e) { /* silent */ }
  };

  const handleScanNow = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/scraper/run`, { async_mode: true }, { headers: getAuthHeader() });
      toast.success("Scan started. Results will appear here in 1-3 minutes.");
      setTimeout(() => { loadPending(); loadRuns(); }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start scan");
    } finally {
      setScanning(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === pending.length) setSelected(new Set());
    else setSelected(new Set(pending.map(r => r.id)));
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) {
      toast.error("Select at least one item");
      return;
    }
    const endpoint = action === "approve" ? "approve-bulk" : "reject-bulk";
    try {
      const res = await axios.post(
        `${API}/scraper/pending/${endpoint}`,
        { resource_ids: Array.from(selected) },
        { headers: getAuthHeader() }
      );
      const count = res.data.approved ?? res.data.rejected ?? 0;
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${count} resource(s)`);
      setSelected(new Set());
      loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    }
  };

  const openConfigEditor = () => {
    if (!config) return;
    setEditAuthors([...(config.authors || [])]);
    setEditSites([...(config.target_sites || [])]);
    setEditKeywords([...(config.extra_keywords || [])]);
    setEditLimit(config.result_limit_per_author || 40);
    setEditSchedEnabled(!!config.schedule_enabled);
    setEditCron(config.schedule_cron || "0 9 * * 1");
    setShowConfig(true);
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(
        `${API}/scraper/config`,
        {
          authors: editAuthors,
          target_sites: editSites,
          extra_keywords: editKeywords,
          result_limit_per_author: Number(editLimit),
          schedule_enabled: editSchedEnabled,
          schedule_cron: editCron,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Scraper settings saved");
      setShowConfig(false);
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingConfig(false);
    }
  };

  const addTo = (list, setList, val, setVal) => {
    const v = (val || "").trim();
    if (!v) return;
    if (!list.includes(v)) setList([...list, v]);
    setVal("");
  };
  const removeAt = (list, setList, i) => setList(list.filter((_, idx) => idx !== i));

  if (loading && !config) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand" />
        <p className="text-muted-foreground mt-2">Loading scraper...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="media-scraper-manager">
      {/* Stats & Actions Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Authors Tracked</p>
            <p className="text-2xl font-bold" data-testid="scraper-authors-count">
              {config?.authors?.length || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Pending Review</p>
            <p className="text-2xl font-bold text-amber-600" data-testid="scraper-pending-count">
              {pendingTotal}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Schedule</p>
            <p className="text-sm font-semibold flex items-center gap-1">
              {config?.schedule_enabled ? (
                <><Clock className="h-4 w-4 text-green-600" /> Active</>
              ) : (
                <><Clock className="h-4 w-4 text-slate-400" /> Paused</>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              cron: <code>{config?.schedule_cron || "—"}</code>
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex flex-col gap-2">
            <Button
              onClick={handleScanNow}
              disabled={scanning}
              className="bg-brand hover:bg-brand-dark"
              data-testid="scraper-scan-now-btn"
            >
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Scan Now
            </Button>
            <Button
              onClick={openConfigEditor}
              variant="outline"
              size="sm"
              data-testid="scraper-settings-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Pending Review List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Pending Review
              {pending.length > 0 && (
                <Badge variant="secondary" className="ml-2">{pending.length}</Badge>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Scraper-discovered resources. Approve to publish, reject to discard.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadPending} data-testid="scraper-refresh-btn">
              <RefreshCcw className="h-4 w-4" />
            </Button>
            {pending.length > 0 && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={selectAll}
                  data-testid="scraper-select-all-btn"
                >
                  {selected.size === pending.length ? "Deselect All" : "Select All"}
                </Button>
                <Button
                  size="sm"
                  disabled={selected.size === 0}
                  onClick={() => bulkAction("approve")}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="scraper-approve-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Approve ({selected.size})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selected.size === 0}
                  onClick={() => bulkAction("reject")}
                  className="text-red-600 hover:bg-red-50 border-red-200"
                  data-testid="scraper-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject ({selected.size})
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="font-medium">Nothing pending</p>
              <p className="text-sm mt-1">Click "Scan Now" to search the web for new articles.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pending.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition ${
                    selected.has(r.id) ? "border-brand bg-brand/5" : ""
                  }`}
                  data-testid={`pending-resource-${r.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleSelect(r.id)}
                    className="mt-1 h-4 w-4 accent-brand"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand hover:underline line-clamp-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.title}
                      </a>
                      <ExternalLink className="h-3 w-3 text-slate-400 flex-shrink-0 mt-1.5" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Badge variant="outline" className="text-xs">{r.source}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{r.resource_type}</Badge>
                      {r.scraper_author && (
                        <span>• found for <strong>{r.scraper_author}</strong></span>
                      )}
                      {r.publication_date && (
                        <span className="flex items-center gap-1">
                          <CalIcon className="h-3 w-3" />
                          {r.publication_date}
                        </span>
                      )}
                    </div>
                    {r.description && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.description}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Runs */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" />
            Recent Scan Runs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No runs yet.</p>
          ) : (
            <div className="space-y-2">
              {runs.map((run) => (
                <div
                  key={run.id}
                  className="flex items-center justify-between p-3 border rounded-lg text-sm"
                  data-testid={`scraper-run-${run.id}`}
                >
                  <div className="flex items-center gap-3">
                    {run.status === "ok" ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium">
                        {new Date(run.started_at).toLocaleString()}
                        <span className="text-muted-foreground ml-2 font-normal">
                          ({run.triggered_by})
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Found {run.total_found} • Pending {run.total_saved_as_pending} • Dup skipped {run.total_skipped_duplicates}
                        {run.duration_sec ? ` • ${Math.round(run.duration_sec)}s` : ""}
                      </p>
                    </div>
                  </div>
                  {run.errors?.length > 0 && (
                    <Badge variant="outline" className="text-red-600 border-red-200">
                      {run.errors.length} error(s)
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scraper Settings</DialogTitle>
            <DialogDescription>
              Configure who to track, where to search, and how often to run.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Authors */}
            <div>
              <Label className="font-semibold">Authors to Track</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Full names (in quotes) to find articles authored by or about.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {editAuthors.map((a, i) => (
                  <Badge key={i} variant="secondary" className="pl-3 pr-1 py-1">
                    {a}
                    <button
                      onClick={() => removeAt(editAuthors, setEditAuthors, i)}
                      className="ml-2 hover:text-red-600"
                      data-testid={`remove-author-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newAuthor}
                  onChange={(e) => setNewAuthor(e.target.value)}
                  placeholder='e.g., "Marshalee Valentine"'
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTo(editAuthors, setEditAuthors, newAuthor, setNewAuthor);
                    }
                  }}
                  data-testid="new-author-input"
                />
                <Button type="button" variant="outline"
                  onClick={() => addTo(editAuthors, setEditAuthors, newAuthor, setNewAuthor)}
                  data-testid="add-author-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Target sites */}
            <div>
              <Label className="font-semibold">Target Sites (optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Domain names to prioritize in searches (e.g., <code>iso.org</code>).
              </p>
              <div className="flex flex-wrap gap-2 mb-2 max-h-32 overflow-y-auto">
                {editSites.map((s, i) => (
                  <Badge key={i} variant="outline" className="pl-3 pr-1 py-1">
                    {s}
                    <button
                      onClick={() => removeAt(editSites, setEditSites, i)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newSite}
                  onChange={(e) => setNewSite(e.target.value)}
                  placeholder="e.g., jamaicaobserver.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTo(editSites, setEditSites, newSite, setNewSite);
                    }
                  }}
                />
                <Button type="button" variant="outline"
                  onClick={() => addTo(editSites, setEditSites, newSite, setNewSite)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Extra keywords */}
            <div>
              <Label className="font-semibold">Extra Keywords (optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Terms paired with author names to narrow results (ISO, HACCP, etc.).
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {editKeywords.map((k, i) => (
                  <Badge key={i} variant="outline" className="pl-3 pr-1 py-1">
                    {k}
                    <button
                      onClick={() => removeAt(editKeywords, setEditKeywords, i)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="e.g., food safety"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTo(editKeywords, setEditKeywords, newKeyword, setNewKeyword);
                    }
                  }}
                />
                <Button type="button" variant="outline"
                  onClick={() => addTo(editKeywords, setEditKeywords, newKeyword, setNewKeyword)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Limit & Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="limit" className="font-semibold">Results / Author</Label>
                <Input
                  id="limit" type="number" min={1} max={200}
                  value={editLimit}
                  onChange={(e) => setEditLimit(e.target.value)}
                  data-testid="result-limit-input"
                />
              </div>
              <div>
                <Label htmlFor="cron" className="font-semibold">Cron Schedule (UTC)</Label>
                <Input
                  id="cron"
                  value={editCron}
                  onChange={(e) => setEditCron(e.target.value)}
                  placeholder="0 9 * * 1"
                  data-testid="cron-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Format: min hour dom month dow (default: Mondays 09:00 UTC)
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label htmlFor="sched-enabled" className="font-semibold">Scheduled Scans</Label>
                <p className="text-xs text-muted-foreground">
                  Run automatically on the cron schedule above.
                </p>
              </div>
              <Switch
                id="sched-enabled"
                checked={editSchedEnabled}
                onCheckedChange={setEditSchedEnabled}
                data-testid="schedule-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button>
            <Button
              onClick={saveConfig}
              disabled={savingConfig}
              className="bg-brand hover:bg-brand-dark"
              data-testid="save-config-btn"
            >
              {savingConfig && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaScraperManager;
